import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { 
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { 
  BarChart, 
  Bar, 
  XAxis, 
  YAxis, 
  CartesianGrid, 
  Tooltip, 
  Legend, 
  ResponsiveContainer,
  LineChart,
  Line,
  ScatterChart,
  Scatter,
  ComposedChart,
  Area
} from 'recharts';
import { 
  TrendingUp, 
  Droplets, 
  Zap, 
  Recycle, 
  Factory, 
  BarChart3,
  Info,
  AlertTriangle,
  Calculator,
  Scale,
  Target
} from 'lucide-react';
import { useAuth } from '@/components/Auth/AuthProvider';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import { useNavigate } from 'react-router-dom';

interface MixedUnitData {
  name: string;
  value: number;
  unit: string;
  category: string;
  date: string;
  source: string;
  normalizedValue?: number;
  conversionFactor?: number;
  targetUnit?: string;
}

interface ConversionFactors {
  [key: string]: { [targetUnit: string]: number };
}

interface AnalysisResult {
  category: string;
  data: MixedUnitData[];
  totalNormalizedValue: number;
  averageNormalizedValue: number;
  efficiencyScore: number;
  recommendations: string[];
  unitDiversity: string[];
}

export const MixedUnitAnalysisCharts = () => {
  const { user } = useAuth();
  const { toast } = useToast();
  const navigate = useNavigate();
  const [mixedUnitData, setMixedUnitData] = useState<MixedUnitData[]>([]);
  const [analysisResults, setAnalysisResults] = useState<AnalysisResult[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedTimeRange, setSelectedTimeRange] = useState('1year');
  const [selectedAnalysisType, setSelectedAnalysisType] = useState('efficiency');
  const [selectedChart, setSelectedChart] = useState<AnalysisResult | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);

  // Conversion factors for common sustainability metrics
  const conversionFactors: ConversionFactors = {
    'kWh': {
      'MJ': 3.6, // 1 kWh = 3.6 MJ
      'GJ': 0.0036, // 1 kWh = 0.0036 GJ
      'tCO2e': 0.0005, // Approximate CO2 equivalent per kWh
      'BTU': 3412.14 // 1 kWh = 3412.14 BTU
    },
    'm³': {
      'L': 1000, // 1 m³ = 1000 L
      'gal': 264.172, // 1 m³ = 264.172 gallons
      'kg': 1000, // 1 m³ water ≈ 1000 kg
      'tCO2e': 0.0003 // Approximate CO2 equivalent for water treatment
    },
    'kg': {
      't': 0.001, // 1 kg = 0.001 t
      'lb': 2.20462, // 1 kg = 2.20462 lb
      'g': 1000, // 1 kg = 1000 g
      'tCO2e': 0.0001 // Approximate CO2 equivalent per kg
    },
    'tCO2e': {
      'kgCO2e': 1000, // 1 tCO2e = 1000 kgCO2e
      'lbCO2e': 2204.62, // 1 tCO2e = 2204.62 lbCO2e
      'gCO2e': 1000000 // 1 tCO2e = 1,000,000 gCO2e
    }
  };

  useEffect(() => {
    if (user) {
      fetchAndProcessData();
    }
  }, [user, selectedTimeRange]);

  const fetchAndProcessData = async () => {
    try {
      setLoading(true);
      
      // Calculate date range
      const endDate = new Date();
      const startDate = new Date();
      switch (selectedTimeRange) {
        case '3months':
          startDate.setMonth(endDate.getMonth() - 3);
          break;
        case '6months':
          startDate.setMonth(endDate.getMonth() - 6);
          break;
        case '1year':
          startDate.setFullYear(endDate.getFullYear() - 1);
          break;
        case '2years':
          startDate.setFullYear(endDate.getFullYear() - 2);
          break;
        default:
          startDate.setFullYear(endDate.getFullYear() - 1);
      }

      // Fetch data from multiple sources
      const [carbonData, esgData, materialsData] = await Promise.all([
        supabase
          .from('carbon_calculations')
          .select('*')
          .eq('user_id', user?.id)
          .gte('calculation_date', startDate.toISOString().split('T')[0])
          .lte('calculation_date', endDate.toISOString().split('T')[0])
          .order('created_at', { ascending: false }),
        supabase
          .from('user_metrics_data')
          .select(`
            *,
            metric:unified_metrics_library(*)
          `)
          .eq('organization_id', user?.id)
          .gte('created_at', startDate.toISOString())
          .lte('created_at', endDate.toISOString())
          .order('created_at', { ascending: false }),
        supabase
          .from('user_materials_data')
          .select(`
            *,
            material:materials_library(*)
          `)
          .eq('organization_id', user?.id)
          .gte('created_at', startDate.toISOString())
          .lte('created_at', endDate.toISOString())
          .order('created_at', { ascending: false })
      ]);

      // Process and normalize data
      const processedData = processMixedUnitData(
        carbonData.data || [],
        esgData.data || [],
        materialsData.data || []
      );

      setMixedUnitData(processedData);
      
      // Perform analysis
      const results = analyzeMixedUnitData(processedData);
      setAnalysisResults(results);
    } catch (error) {
      console.error('Error fetching data:', error);
      toast({
        title: 'Error',
        description: 'Failed to fetch data for analysis',
        variant: 'destructive'
      });
    } finally {
      setLoading(false);
    }
  };

  const processMixedUnitData = (
    carbonData: any[],
    esgData: any[],
    materialsData: any[]
  ): MixedUnitData[] => {
    const processedData: MixedUnitData[] = [];

    // Process carbon data
    carbonData.forEach(item => {
      processedData.push({
        name: item.activity_name || 'Unknown Activity',
        value: Number(item.amount) || 0,
        unit: item.unit || 'tCO2e',
        category: item.calculation_type || 'carbon',
        date: item.calculation_date || item.created_at,
        source: 'Carbon Calculations'
      });
    });

    // Process ESG data
    esgData.forEach(item => {
      processedData.push({
        name: item.metric_name || item.name || 'Unknown Metric',
        value: Number(item.custom_value) || 0,
        unit: item.unit || 'unit',
        category: item.category || 'esg',
        date: item.created_at,
        source: 'ESG Metrics'
      });
    });

    // Process materials data
    materialsData.forEach(item => {
      processedData.push({
        name: item.material?.name || item.name || 'Unknown Material',
        value: Number(item.custom_value) || 0,
        unit: item.unit || 'kg',
        category: item.material?.category || 'materials',
        date: item.created_at,
        source: 'Materials Data'
      });
    });

    // Add normalized values and conversion factors
    return processedData.map(item => {
      const normalized = normalizeValue(item.value, item.unit, 'tCO2e');
      return {
        ...item,
        normalizedValue: normalized.value,
        conversionFactor: normalized.factor,
        targetUnit: normalized.targetUnit
      };
    });
  };

  const normalizeValue = (value: number, fromUnit: string, toUnit: string) => {
    if (fromUnit === toUnit) {
      return { value, factor: 1, targetUnit: toUnit };
    }

    const factors = conversionFactors[fromUnit];
    if (factors && factors[toUnit]) {
      return {
        value: value * factors[toUnit],
        factor: factors[toUnit],
        targetUnit: toUnit
      };
    }

    // If no direct conversion, try to find a path through common units
    if (fromUnit === 'kWh' && toUnit === 'tCO2e') {
      return {
        value: value * 0.0005, // Approximate conversion
        factor: 0.0005,
        targetUnit: toUnit
      };
    }

    if (fromUnit === 'm³' && toUnit === 'tCO2e') {
      return {
        value: value * 0.0003, // Approximate conversion for water
        factor: 0.0003,
        targetUnit: toUnit
      };
    }

    // Default: return original value with factor 1
    return { value, factor: 1, targetUnit: fromUnit };
  };

  const analyzeMixedUnitData = (data: MixedUnitData[]): AnalysisResult[] => {
    const categories = [...new Set(data.map(item => item.category))];
    const results: AnalysisResult[] = [];

    categories.forEach(category => {
      const categoryData = data.filter(item => item.category === category);
      const unitDiversity = [...new Set(categoryData.map(item => item.unit))];
      
      // Calculate normalized totals and averages
      const normalizedValues = categoryData
        .filter(item => item.normalizedValue !== undefined)
        .map(item => item.normalizedValue!);
      
      const totalNormalizedValue = normalizedValues.reduce((sum, val) => sum + val, 0);
      const averageNormalizedValue = normalizedValues.length > 0 ? totalNormalizedValue / normalizedValues.length : 0;

      // Calculate efficiency score (lower is better for most metrics)
      const efficiencyScore = calculateEfficiencyScore(categoryData, category);

      // Generate recommendations
      const recommendations = generateRecommendations(categoryData, category, efficiencyScore);

      results.push({
        category,
        data: categoryData,
        totalNormalizedValue,
        averageNormalizedValue,
        efficiencyScore,
        recommendations,
        unitDiversity
      });
    });

    return results;
  };

  const calculateEfficiencyScore = (data: MixedUnitData[], category: string): number => {
    if (data.length === 0) return 0;

    let score = 0;
    const totalDataPoints = data.length;

    data.forEach(item => {
      // Different scoring logic for different categories
      switch (category.toLowerCase()) {
        case 'carbon':
          // Lower values are better for carbon
          score += Math.max(0, 100 - (item.normalizedValue || item.value) * 100);
          break;
        case 'energy':
          // Lower values are better for energy
          score += Math.max(0, 100 - (item.normalizedValue || item.value) * 10);
          break;
        case 'water':
          // Lower values are better for water
          score += Math.max(0, 100 - (item.normalizedValue || item.value) * 0.1);
          break;
        case 'materials':
          // Lower values are better for materials
          score += Math.max(0, 100 - (item.normalizedValue || item.value) * 0.01);
          break;
        default:
          // For other categories, assume lower is better
          score += Math.max(0, 100 - (item.normalizedValue || item.value));
      }
    });

    return Math.min(100, Math.max(0, score / totalDataPoints));
  };

  const generateRecommendations = (
    data: MixedUnitData[], 
    category: string, 
    efficiencyScore: number
  ): string[] => {
    const recommendations: string[] = [];
    const unitDiversity = [...new Set(data.map(item => item.unit))];

    // General recommendations based on efficiency score
    if (efficiencyScore < 30) {
      recommendations.push('Immediate action required to improve efficiency');
      recommendations.push('Consider implementing best practices and monitoring systems');
    } else if (efficiencyScore < 60) {
      recommendations.push('Moderate improvements needed for better performance');
      recommendations.push('Review current processes and identify optimization opportunities');
    } else if (efficiencyScore < 80) {
      recommendations.push('Good performance, focus on continuous improvement');
      recommendations.push('Set stretch goals for next performance cycle');
    } else {
      recommendations.push('Excellent performance, maintain current standards');
      recommendations.push('Share best practices with other teams');
    }

    // Category-specific recommendations
    if (unitDiversity.length > 1) {
      recommendations.push(`Standardize units across ${category} measurements for better comparison`);
    }

    if (category.toLowerCase() === 'carbon' && efficiencyScore < 70) {
      recommendations.push('Implement carbon reduction initiatives');
      recommendations.push('Consider renewable energy sources');
    }

    if (category.toLowerCase() === 'energy' && efficiencyScore < 70) {
      recommendations.push('Conduct energy audit to identify waste');
      recommendations.push('Implement energy-efficient technologies');
    }

    if (category.toLowerCase() === 'water' && efficiencyScore < 70) {
      recommendations.push('Implement water conservation measures');
      recommendations.push('Consider water recycling systems');
    }

    return recommendations;
  };

  const renderMixedUnitChart = (result: AnalysisResult) => {
    const chartData = result.data.map(item => ({
      name: item.name,
      originalValue: item.value,
      normalizedValue: item.normalizedValue || item.value,
      unit: item.unit,
      normalizedUnit: result.category === 'carbon' ? 'tCO2e' : 'normalized'
    }));

    return (
      <ResponsiveContainer width="100%" height={300}>
        <ComposedChart data={chartData}>
          <CartesianGrid strokeDasharray="3 3" />
          <XAxis dataKey="name" angle={-45} textAnchor="end" height={80} />
          <YAxis yAxisId="left" />
          <YAxis yAxisId="right" orientation="right" />
          <Tooltip 
            formatter={(value: any, name: string) => [
              `${value.toFixed(2)} ${name === 'originalValue' ? 'original unit' : 'normalized'}`,
              name === 'originalValue' ? 'Original Value' : 'Normalized Value'
            ]}
          />
          <Legend />
          <Bar 
            yAxisId="left"
            dataKey="originalValue" 
            fill="#8884d8" 
            name="Original Value"
          />
          <Line 
            yAxisId="right"
            type="monotone" 
            dataKey="normalizedValue" 
            stroke="#82ca9d" 
            strokeWidth={2}
            name="Normalized Value"
          />
        </ComposedChart>
      </ResponsiveContainer>
    );
  };

  const renderEfficiencyChart = (result: AnalysisResult) => {
    const chartData = [
      { name: 'Efficiency Score', value: result.efficiencyScore, fill: '#10b981' },
      { name: 'Improvement Needed', value: 100 - result.efficiencyScore, fill: '#ef4444' }
    ];

    return (
      <ResponsiveContainer width="100%" height={300}>
        <BarChart data={chartData} layout="horizontal">
          <CartesianGrid strokeDasharray="3 3" />
          <XAxis type="number" domain={[0, 100]} />
          <YAxis dataKey="name" type="category" />
          <Tooltip formatter={(value: any) => [`${value.toFixed(1)}%`, 'Percentage']} />
          <Bar dataKey="value" fill="#8884d8" />
        </BarChart>
      </ResponsiveContainer>
    );
  };

  if (loading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Mixed Unit Analysis</CardTitle>
          <CardDescription>Analyzing data with different units...</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex items-center justify-center h-64">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-green-600"></div>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header and Controls */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Calculator className="h-5 w-5" />
            Mixed Unit Analysis
          </CardTitle>
          <CardDescription>
            Analyze and compare data with different units using normalization and conversion factors
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex flex-wrap gap-4 items-center">
            <div className="flex items-center gap-2">
              <span className="text-sm font-medium">Time Range:</span>
              <Select value={selectedTimeRange} onValueChange={setSelectedTimeRange}>
                <SelectTrigger className="w-32">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="3months">3 Months</SelectItem>
                  <SelectItem value="6months">6 Months</SelectItem>
                  <SelectItem value="1year">1 Year</SelectItem>
                  <SelectItem value="2years">2 Years</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <Button 
              variant="outline" 
              size="sm"
              onClick={fetchAndProcessData}
            >
              Refresh Analysis
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Analysis Results */}
      {analysisResults.length === 0 ? (
        <Card>
          <CardContent className="flex items-center justify-center h-64">
            <div className="text-center">
              <AlertTriangle className="h-12 w-12 text-yellow-500 mx-auto mb-4" />
              <h3 className="text-lg font-semibold mb-2">No Data Available</h3>
              <p className="text-gray-600 mb-4">
                No data found for the selected time range.
              </p>
              <Button onClick={() => navigate('/data-management')}>
                Add Data
              </Button>
            </div>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-6">
          {analysisResults.map((result, index) => (
            <Card key={`${result.category}-${index}`} className="hover:shadow-md transition-shadow">
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle className="text-lg capitalize">{result.category} Analysis</CardTitle>
                    <CardDescription>
                      Efficiency Score: {result.efficiencyScore.toFixed(1)}%
                    </CardDescription>
                  </div>
                  <div className="flex items-center gap-2">
                    <Badge variant="outline" className="text-blue-600 bg-blue-50">
                      <Scale className="h-4 w-4 mr-1" />
                      {result.unitDiversity.length} Units
                    </Badge>
                    <Button 
                      variant="outline" 
                      size="sm"
                      onClick={() => {
                        setSelectedChart(result);
                        setIsModalOpen(true);
                      }}
                    >
                      View Details
                    </Button>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                <Tabs defaultValue="mixed" className="w-full">
                  <TabsList className="grid w-full grid-cols-2">
                    <TabsTrigger value="mixed">Mixed Unit Chart</TabsTrigger>
                    <TabsTrigger value="efficiency">Efficiency Analysis</TabsTrigger>
                  </TabsList>
                  
                  <TabsContent value="mixed" className="mt-4">
                    <div className="mb-4">
                      <h4 className="font-semibold mb-2">Data Comparison (Original vs Normalized)</h4>
                      <p className="text-sm text-gray-600">
                        Blue bars show original values, green line shows normalized values for comparison
                      </p>
                    </div>
                    {renderMixedUnitChart(result)}
                  </TabsContent>
                  
                  <TabsContent value="efficiency" className="mt-4">
                    <div className="mb-4">
                      <h4 className="font-semibold mb-2">Efficiency Score Breakdown</h4>
                      <p className="text-sm text-gray-600">
                        Current performance vs. improvement potential
                      </p>
                    </div>
                    {renderEfficiencyChart(result)}
                  </TabsContent>
                </Tabs>

                {/* Summary Metrics */}
                <div className="mt-6 grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div className="text-center p-3 bg-blue-50 rounded-lg">
                    <p className="text-2xl font-bold text-blue-600">
                      {result.totalNormalizedValue.toFixed(2)}
                    </p>
                    <p className="text-xs text-gray-600">Total Normalized</p>
                  </div>
                  <div className="text-center p-3 bg-green-50 rounded-lg">
                    <p className="text-2xl font-bold text-green-600">
                      {result.averageNormalizedValue.toFixed(2)}
                    </p>
                    <p className="text-xs text-gray-600">Average Normalized</p>
                  </div>
                  <div className="text-center p-3 bg-purple-50 rounded-lg">
                    <p className="text-2xl font-bold text-purple-600">
                      {result.data.length}
                    </p>
                    <p className="text-xs text-gray-600">Data Points</p>
                  </div>
                </div>

                {/* Key Recommendations */}
                <div className="mt-4">
                  <h4 className="font-semibold mb-2">Key Recommendations</h4>
                  <div className="space-y-2">
                    {result.recommendations.slice(0, 3).map((rec, idx) => (
                      <div key={idx} className="flex items-start gap-2 p-2 bg-gray-50 rounded">
                        <Target className="h-4 w-4 text-green-600 mt-0.5 flex-shrink-0" />
                        <span className="text-sm">{rec}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Detail Modal */}
      <Dialog open={isModalOpen} onOpenChange={setIsModalOpen}>
        <DialogContent className="max-w-6xl">
          <DialogHeader>
            <DialogTitle>
              {selectedChart ? `${selectedChart.category} Analysis - Detailed View` : 'Analysis Details'}
            </DialogTitle>
            <DialogDescription>
              Comprehensive analysis including unit conversions, efficiency metrics, and recommendations
            </DialogDescription>
          </DialogHeader>
          
          {selectedChart && (
            <div className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <h4 className="font-semibold mb-3">Performance Metrics</h4>
                  <div className="space-y-3">
                    <div className="flex justify-between p-2 bg-gray-50 rounded">
                      <span>Efficiency Score:</span>
                      <Badge variant={selectedChart.efficiencyScore >= 70 ? 'default' : 'destructive'}>
                        {selectedChart.efficiencyScore.toFixed(1)}%
                      </Badge>
                    </div>
                    <div className="flex justify-between p-2 bg-gray-50 rounded">
                      <span>Total Normalized Value:</span>
                      <span className="font-medium">
                        {selectedChart.totalNormalizedValue.toFixed(2)}
                      </span>
                    </div>
                    <div className="flex justify-between p-2 bg-gray-50 rounded">
                      <span>Average Normalized Value:</span>
                      <span className="font-medium">
                        {selectedChart.averageNormalizedValue.toFixed(2)}
                      </span>
                    </div>
                    <div className="flex justify-between p-2 bg-gray-50 rounded">
                      <span>Unit Diversity:</span>
                      <span className="font-medium">{selectedChart.unitDiversity.length} units</span>
                    </div>
                  </div>
                </div>
                
                <div>
                  <h4 className="font-semibold mb-3">Unit Analysis</h4>
                  <div className="space-y-2">
                    {selectedChart.unitDiversity.map((unit, idx) => (
                      <div key={idx} className="flex items-center gap-2 p-2 bg-blue-50 rounded">
                        <Info className="h-4 w-4 text-blue-600" />
                        <span className="text-sm font-medium">{unit}</span>
                        <Badge variant="outline" className="text-xs">
                          {selectedChart.data.filter(item => item.unit === unit).length} data points
                        </Badge>
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              <div>
                <h4 className="font-semibold mb-3">All Recommendations</h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  {selectedChart.recommendations.map((rec, idx) => (
                    <div key={idx} className="flex items-start gap-2 p-3 bg-green-50 rounded border border-green-200">
                      <Target className="h-4 w-4 text-green-600 mt-0.5 flex-shrink-0" />
                      <span className="text-sm">{rec}</span>
                    </div>
                  ))}
                </div>
              </div>

              <div>
                <h4 className="font-semibold mb-3">Data Points with Unit Conversions</h4>
                <div className="max-h-64 overflow-y-auto">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                    {selectedChart.data.map((item, index) => (
                      <div key={index} className="p-3 border rounded text-sm">
                        <div className="font-medium">{item.name}</div>
                        <div className="grid grid-cols-2 gap-2 mt-2 text-xs">
                          <div>
                            <span className="text-gray-600">Original:</span>
                            <div className="font-medium">{item.value.toFixed(2)} {item.unit}</div>
                          </div>
                          <div>
                            <span className="text-gray-600">Normalized:</span>
                            <div className="font-medium">
                              {(item.normalizedValue || item.value).toFixed(2)} {item.targetUnit || 'normalized'}
                            </div>
                          </div>
                        </div>
                        <div className="text-xs text-gray-500 mt-1">
                          {new Date(item.date).toLocaleDateString()} • {item.source}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};
